import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { collectClientInfo } from "@/lib/clientInfo";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { beginSessionChange, getSessionGeneration } from "@/lib/sessionGeneration";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinOk, setPinOk] = useState(() => sessionStorage.getItem("cc_pin_ok") === "yes");

  const refresh = useCallback(async () => {
    const generation = getSessionGeneration();
    // One-time cleanup of legacy credentials. Authentication now lives only in HttpOnly cookies.
    localStorage.removeItem("cc_token");
    try {
      const res = await api.get("/auth/me");
      if (generation === getSessionGeneration()) setUser(res.data);
    } catch (e) {
      localStorage.removeItem("cc_token");
      // Only drop the session on a real auth failure. Wallet refreshes now
      // fire often (after every action / on focus), so a transient network
      // blip must NOT log the user out of the UI. True 401s are handled by
      // the api interceptor's "playden-session-expired" path.
      if (e?.response?.status === 401 && generation === getSessionGeneration()) setUser(null);
    } finally {
      if (generation === getSessionGeneration()) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const expired = () => { setUser(null); setPinOk(false); sessionStorage.removeItem("cc_pin_ok"); queryClient.clear(); };
    window.addEventListener("playden-session-expired", expired);
    return () => window.removeEventListener("playden-session-expired", expired);
  }, [queryClient]);

  // Keep the wallet/credits fresh everywhere with the cheapest possible
  // signals: an instant refresh after any state-changing action (dispatched
  // by the api interceptor), a refresh when the tab regains focus, and a slow
  // 30s safety poll for server-side changes (e.g. being gifted credits) while
  // the tab is visible. This replaces Layout's old 8s /auth/me poll.
  useEffect(() => {
    if (!user?.id) return;
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("playden-wallet-refresh", refresh);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const safety = setInterval(onVisible, 30000);
    return () => {
      window.removeEventListener("playden-wallet-refresh", refresh);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(safety);
    };
  }, [user?.id, refresh]);

  const login = async (email, password) => {
    const generation = beginSessionChange();
    try {
      const res = await api.post("/auth/login", { email, password });
      const verified = await api.get("/auth/me", { _skipSessionRefresh: true });
      if (generation !== getSessionGeneration()) throw new Error("Sign-in was superseded by another session change.");
      queryClient.clear();
      sessionStorage.removeItem("playden_tos_ok");
      if (res.data.pin_required) {
        // Admin must clear the PIN keypad before the app unlocks.
        sessionStorage.removeItem("cc_pin_ok");
        setPinOk(false);
      } else {
        sessionStorage.setItem("cc_pin_ok", "yes");
        setPinOk(true);
      }
      setUser(verified.data);
      toast.success(`Welcome back, ${verified.data.display_name}`);
      return verified.data;
    } catch (e) {
      toast.error(apiError(e, "Login failed"));
      throw e;
    } finally {
      if (generation === getSessionGeneration()) setLoading(false);
    }
  };

  const register = async (email, password, display_name, date_of_birth, tos_version) => {
    const generation = beginSessionChange();
    try {
      let client_info = null;
      try { client_info = await collectClientInfo(); } catch (_) { /* best-effort */ }
      await api.post("/auth/register", { email, password, display_name, tos_accepted: true, tos_version, client_info, date_of_birth });
      const verified = await api.get("/auth/me", { _skipSessionRefresh: true });
      if (generation !== getSessionGeneration()) throw new Error("Registration was superseded by another session change.");
      queryClient.clear();
      sessionStorage.setItem("cc_pin_ok", "yes");
      // ToS was accepted via the registration checkbox — recorded server-side.
      sessionStorage.setItem("playden_tos_ok", tos_version);
      setPinOk(true);
      setUser(verified.data);
      toast.success(`Welcome, ${verified.data.display_name}!`);
      return verified.data;
    } catch (e) {
      toast.error(apiError(e, "Registration failed"));
      throw e;
    } finally {
      if (generation === getSessionGeneration()) setLoading(false);
    }
  };

  const verifyPin = async (pin) => {
    await api.post("/auth/verify-pin", { pin });
    sessionStorage.setItem("cc_pin_ok", "yes");
    setPinOk(true);
  };

  const logout = async () => {
    beginSessionChange();
    try { await api.post("/auth/logout"); }
    catch (e) { toast.error(apiError(e, "Sign out failed. Please try again.")); return false; }
    localStorage.removeItem("cc_token");
    sessionStorage.removeItem("cc_pin_ok");
    sessionStorage.removeItem("playden_user_agreement_v1");
    sessionStorage.removeItem("playden_tos_ok");
    setPinOk(false);
    setUser(null);
    queryClient.clear();
    toast.success("Signed out");
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, pinOk, login, register, logout, refresh, verifyPin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
